import { notification, message } from 'antd'
import * as queryString from 'query-string'
import moment from 'moment'
import type { Moment } from 'moment'
import { TFieldType } from '@interfaces/common/common.Interface'

export function showNoti(type: 'success' | 'error' | 'warning' | 'info', text: string) {
  notification[type]({
    message: 'Thông báo',
    description: text,
  })
}

export function showMessage(type: 'info' | 'success' | 'error' | 'warning' | 'loading', text: string) {
  message[type](text, 4.5)
}

export function isEmailValid(email: string) {
  let re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(email)
}

export function isPhoneValid(phone: string) {
  try {
    const phoneUtil = require('google-libphonenumber').PhoneNumberUtil.getInstance()
    const phoneNumber = phoneUtil.parse(phone, 'VN')
    return phoneUtil.isValidNumber(phoneNumber, 'VN')
  } catch (e) {
    return false
  }
}

export function handleError(err: any) {
  if (err.message) showMessage('error', err.message + ' (' + err.code + ')')
  else {
    console.error(err)
  }
}

export function getBase64(file: any) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = (error) => reject(error)
  })
}

export function showPrice(price: number | undefined) {
  if (price === undefined) return ""
  return Number(price).toLocaleString('en-US', { maximumFractionDigits: 6, useGrouping: true })
}

export function numberExcel(price: number | undefined) {
  if (price === undefined) return ""
  const formattedNumber = Number(price).toLocaleString('en-US', {
    maximumFractionDigits: 6,
    useGrouping: true,
  });
  const result = formattedNumber.replace(/\./g, '@').replace(/,/g, '.').replace(/@/g, ',');
  return result;
}


export function convertGender(gender: number) {
  if (gender === 1) return 'Nam'
  if (gender === 2) return 'Nữ'
  return ''
}

export const handleChangePaging = ({
  history,
  pathname,
  search,
  page,
  pageSize,
}: {
  history: any
  pathname: string
  search: string
  page: number
  pageSize?: number
}) => {
  const parsed: any = queryString.parse(search)
  parsed.page = page
  if (pageSize) {
    parsed.pageSize = pageSize
    // Remove old limit if it exists
    if (parsed.limit) delete parsed.limit
  }
  const stringified = queryString.stringify(parsed, { skipEmptyString: true })
  history.push(pathname + '?' + stringified)
}


export function formatter(value: number | undefined) {
  return Number(value).toLocaleString('en-US', { maximumFractionDigits: 6, useGrouping: true })
}

export function formatterSoKhoi(value: number | undefined) {
  return Number(value).toLocaleString('en-US', { maximumFractionDigits: 6, useGrouping: true })
}

export const formatterNumber = (val: number | string | undefined | null): string => {
  if (!val) return "0";
  return `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".").replace(/\.(?=\d{0,2}$)/g, ",");
}


export const openInNewTab = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

export const openInTab = (url: string) => {
  window.open(url, "_self");
};

export const delay = async (value: number) => {
  await new Promise(resolve => setTimeout(resolve, value));
};

// Hàm kiểm tra quyền Clipboard
export async function requestClipboardPermission(): Promise<boolean> {
  try {
    // @ts-ignore
    const permissionStatus = await navigator.permissions.query({ name: 'clipboard-write' });

    if (permissionStatus.state === "granted" || permissionStatus.state === "prompt") {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    // Silently fail - clipboard permission không available trong một số browsers
    return false;
  }
}

// Hàm sao chép văn bản
export async function copyToClipboardWithPermission(text: string): Promise<void> {
  const hasPermission = await requestClipboardPermission();
  if (!hasPermission) {
    throw new Error("Không có quyền truy cập Clipboard.");
  }
  await navigator.clipboard.writeText(text);
}

// Hàm sao chép văn bản (wrapper đơn giản)
export async function copyText(text: string): Promise<boolean> {
  try {
    await copyToClipboardWithPermission(text);
    return true;
  } catch (error) {
    return false;
  }
}


export const fomartDateTime = (date: string | Date) => {
  return date ? moment(date).format('DD/MM/YYYY | HH:mm') : ''
}
export const fomartDate = (date: string | Date, format?: string) => {
  return date ? moment(date).format(format || 'DD/MM/YYYY') : ''
}
export const dateTimeNow = () => {
  return moment().format('DDMMYYYY_HHmmss')
}

export const formatVnd = (value: number) => {
  return value ? value.toLocaleString('en-US', { maximumFractionDigits: 6, useGrouping: true }) : ''
}
export const isHaveValue = (value: unknown): boolean => {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string' && value.trim() === '') return false;
  return true;
}

export const hasDataChanged = (
  currentData: any,
  originalData: any,
  ignoreFields: string[] = ['id', 'createdAt', 'updatedAt', 'createdBy', 'updatedBy']
): boolean => {
  if (!originalData) return true;
  
  return Object.keys(currentData).some(key => {
    // Ignore metadata fields for comparison
    if (ignoreFields.includes(key)) {
      return false;
    }
    
    const currentValue = currentData[key];
    const originalValue = originalData[key];
    
    // Handle null/undefined comparison
    if (currentValue === null && originalValue === null) return false;
    if (currentValue === undefined && originalValue === undefined) return false;
    if (currentValue === null && originalValue === undefined) return false;
    if (currentValue === undefined && originalValue === null) return false;
    
    // Handle number comparison
    if (typeof currentValue === 'number' && typeof originalValue === 'number') {
      return currentValue !== originalValue;
    }
    
    return currentValue !== originalValue;
  });
}


export const getDisplayText = (value: unknown, type: TFieldType) => {
  if (value === null || value === undefined) return '';
  if (type === 'text') return String(value);
  if (type === 'number') return formatterNumber(value as number);
  if (type === 'date') return fomartDate(value as string | Date);
  if (type === 'dateTime') return fomartDateTime(value as string | Date);
  if (type === 'currency') return formatterNumber(value as number);
  return String(value);
}

export const pickStr = (queryParams: queryString.ParsedQuery<string>, key: string) => {
  const v = queryParams[key]
  if (v == null) return undefined
  if (typeof v === 'string' && v.trim() !== '') return v
  if (Array.isArray(v)) {
    const first = v[0]
    if (typeof first === 'string' && first.trim() !== '') return first
  }
  return undefined
}

export const pickNum = (queryParams: queryString.ParsedQuery<string>, key: string) => {
  const s = pickStr(queryParams, key)
  if (s === undefined) return undefined
  const n = Number(s)
  return Number.isFinite(n) ? n : undefined
}

export const toNum = (v: unknown): number | undefined => {
  if (v === undefined || v === null || v === '') return undefined
  const n = typeof v === 'string' ? parseFloat(v) : Number(v)
  return Number.isFinite(n) ? n : undefined
}

/** Giá trị đầu tiên từ query-string `parse` (một key) */
export const firstQueryParamValue = (
  v: string | string[] | (string | null)[] | null | undefined
): string | undefined => {
  if (v === null || v === undefined) return undefined
  if (Array.isArray(v)) {
    const first = v[0]
    if (first === null || first === undefined) return undefined
    const s = String(first).trim()
    return s !== '' ? s : undefined
  }
  const s = String(v).trim()
  return s !== '' ? s : undefined
}

/** Chuẩn hóa giá trị cho DatePicker antd v4 (moment) */
export const parseMomentOptional = (value: unknown): Moment | undefined => {
  if (value == null || value === '') return undefined
  if (moment.isMoment(value)) return value
  const m = moment(value)
  return m.isValid() ? m : undefined
}

/** Form/API: moment hoặc chuỗi/ngày → `YYYY-MM-DD`, không hợp lệ thì undefined */
export const toIsoDateStringOrUndefined = (value: unknown): string | undefined => {
  if (value == null || value === '') return undefined
  if (moment.isMoment(value)) return value.format('YYYY-MM-DD')
  const m = moment(value)
  return m.isValid() ? m.format('YYYY-MM-DD') : undefined
}





